import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CATEGORY_KINDS, CATEGORY_KIND_LABELS } from '../../lib/portalAssets/categoryKinds';
import { STATUS_FILTER_LABELS } from '../../lib/portalAssets/pageState';
import { SORT_OPTIONS, type SortOptionId } from '../../lib/portalAssets/sortOptions';
import {
  chooseSort,
  DATE_NEEDS_DATE_SORT_SENTENCE,
  dateRangeAvailable,
  narrowMode,
  searchState,
  SEARCH_DISABLES_FILTERS_SENTENCE,
  UNREVIEWED_DISABLES_FILTERS_SENTENCE,
} from '../../lib/portalAssets/sortSearchView';
import { LIFECYCLE_LABELS, MEDIA_TYPE_LABELS } from '../../lib/portalAssets/staffAllView';
import type { PortalAssetLifecycle, PortalAssetMediaType, PortalAssetReviewStatus } from '../../types/portalAsset';

// THE ONE FILTER BAR (Run C, 2026-10-12). Search + sort on the first row, every filter in a predictable grid beneath, actions
// apart. Used by THREE pages — the staff cross-account list, the account page's Assets tab and the club's own Assets page —
// because the first two had built nearly the same filter set twice and a third copy would have been worse. A filter added
// here appears on all three; a page that must NOT have one (the club, for the Kyck-review filter) is told so by a prop, not
// by a separate copy.
//
// ⚠️ THE `roster` SLOT holds SCOPE controls: brand / group / account manager / club on the staff cross-account page (they need
// the whole roster, which only staff hold, and they change what the query is rather than narrow it), and the club's own
// Location picker on its page. The account tab fills nothing: the account IS the scope.
// ⚠️ NATIVE <select>, NOT SelectField: the club's page lives inside `.kyck-portal`, and a Radix select portals its list to
// <body>, outside that scope, in the staff app's tokens (a white list on a dark portal). A native select needs no portal, and
// its colours come from the same per-mode tokens as everything else on the page. Same reason the club page already used one.
// ⚠️ ONE HEIGHT, ONE LABEL TREATMENT: every control is h-10 with its label above in the same small type. The date pair used to
// carry a label while the selects did not, which is why that row was taller than the others.
// ⚠️ While a search or "Not reviewed by Kyck" is running the other controls are DISABLED, visibly, with a sentence — never
// silently ignored (sortSearchView.ts narrowMode). Their values stay in the form, so turning it off restores the page.

export type AssetFilterValues = {
  search: string;
  sort: SortOptionId;
  status: PortalAssetReviewStatus | '';
  mediaType: PortalAssetMediaType | '';
  lifecycle: PortalAssetLifecycle | '';
  categoryId: string;
  categoryKind: string;
  from: string;
  to: string;
  kyckUnreviewed: boolean;
};

type Option = { value: string; label: string };

const LABEL_CLASS = 'block text-xs font-medium text-muted-foreground';
const CONTROL_CLASS =
  'mt-1 block h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground disabled:cursor-not-allowed disabled:opacity-50';
/** Exported so a host can lay its own roster controls out on the same grid. */
export const FILTER_GRID_CLASS = 'grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4';

/** A label above a control; for controls (a Combobox) that are not a plain input. */
export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <span className={LABEL_CLASS}>{label}</span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** A labelled native select, exported so a host can put its own scope control (the club's Location) in the `roster` slot. */
export function FilterSelect({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  disabled?: boolean;
}) {
  return (
    <label className="min-w-0">
      <span className={LABEL_CLASS}>{label}</span>
      <select className={CONTROL_CLASS} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const SORT_CHOICES: Option[] = SORT_OPTIONS.map((o) => ({ value: o.id as string, label: o.label }));
const STATUS_CHOICES: Option[] = [
  { value: '', label: 'Any status' },
  ...(Object.keys(STATUS_FILTER_LABELS) as PortalAssetReviewStatus[]).map((s) => ({ value: s, label: STATUS_FILTER_LABELS[s] })),
];
const KIND_CHOICES: Option[] = [
  { value: '', label: 'Any category type' },
  ...CATEGORY_KINDS.map((k) => ({ value: k, label: CATEGORY_KIND_LABELS[k] })),
];
const MEDIA_CHOICES: Option[] = [
  { value: '', label: 'Photos and videos' },
  ...(Object.keys(MEDIA_TYPE_LABELS) as PortalAssetMediaType[]).map((m) => ({ value: m, label: MEDIA_TYPE_LABELS[m] })),
];
const LIFECYCLE_CHOICES: Option[] = [
  { value: '', label: 'Any processing state' },
  ...(Object.keys(LIFECYCLE_LABELS) as PortalAssetLifecycle[]).map((l) => ({ value: l, label: LIFECYCLE_LABELS[l] })),
];

export function AssetFilterBar<F extends AssetFilterValues>({
  form,
  setForm,
  categories,
  showCategory = true,
  showKyckFilter,
  roster,
  filtered,
  onClear,
  actions,
}: {
  form: F;
  setForm: Dispatch<SetStateAction<F>>;
  /** The categories to offer (WITHOUT the "Any category" entry). null/empty while they load or when there are none. */
  categories: Option[] | null;
  /** The staff cross-account page hides the category until a brand is chosen (a category belongs to one brand). */
  showCategory?: boolean;
  /** ⚠️ STAFF ONLY. The club's page passes false: a club has no business filtering by Kyck's review. */
  showKyckFilter: boolean;
  /** Scope controls: the staff page's brand / group / manager / club, or the club's Location picker. */
  roster?: ReactNode;
  /** Whether any filter is narrowing the list (shows "Clear filters"). */
  filtered: boolean;
  onClear: () => void;
  /** Not filters: Upload, Recently deleted. Rendered apart, right-aligned. */
  actions?: ReactNode;
}) {
  const state = searchState(form.search);
  const mode = narrowMode(form);
  const narrow = mode !== null;
  const datesOk = dateRangeAvailable(form.sort, narrow);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="flex flex-wrap items-end gap-3">
          <label className="min-w-[16rem] max-w-xl flex-1">
            <span className={LABEL_CLASS}>Search</span>
            <Input
              type="search"
              className="mt-1"
              value={form.search}
              onChange={(e) => setForm((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search by name, title or tag"
              aria-label="Search assets"
            />
          </label>
          <div className="w-full sm:w-52">
            <FilterSelect
              label="Sort"
              value={form.sort}
              onChange={(v) => setForm((f) => chooseSort(f, v as SortOptionId))}
              options={SORT_CHOICES}
              disabled={narrow}
            />
          </div>
        </div>
        {mode === 'search' && <p className="text-xs text-muted-foreground">{SEARCH_DISABLES_FILTERS_SENTENCE}</p>}
        {mode === 'unreviewed' && <p className="text-xs text-muted-foreground">{UNREVIEWED_DISABLES_FILTERS_SENTENCE}</p>}
        {state.hint && <p className="text-xs text-muted-foreground">{state.hint}</p>}
      </div>

      {roster && <div className={FILTER_GRID_CLASS}>{roster}</div>}

      <div className={FILTER_GRID_CLASS}>
        {showCategory && (
          <FilterSelect
            label="Category"
            value={form.categoryId}
            // ⚠️ A category and a category type exclude each other: two array-contains clauses cannot share a query.
            onChange={(v) => setForm((f) => ({ ...f, categoryId: v, categoryKind: v ? '' : f.categoryKind }))}
            options={[{ value: '', label: 'Any category' }, ...(categories ?? [])]}
            disabled={narrow}
          />
        )}
        <FilterSelect
          label="Category type"
          value={form.categoryKind}
          onChange={(v) => setForm((f) => ({ ...f, categoryKind: v, categoryId: v ? '' : f.categoryId }))}
          options={KIND_CHOICES}
          disabled={narrow}
        />
        <FilterSelect
          label="Review status"
          value={form.status}
          onChange={(v) => setForm((f) => ({ ...f, status: v as F['status'] }))}
          options={STATUS_CHOICES}
          disabled={narrow}
        />
        {showKyckFilter && (
          <div className="min-w-0">
            <span className={LABEL_CLASS}>Kyck review</span>
            <label className="mt-1 flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-foreground">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.kyckUnreviewed}
                // ⚠️ Disabled only while a SEARCH is running (search wins); the value is kept either way.
                disabled={mode === 'search'}
                onChange={(e) => setForm((f) => ({ ...f, kyckUnreviewed: e.target.checked }))}
              />
              Not reviewed by Kyck
            </label>
          </div>
        )}
        <FilterSelect
          label="Photos and videos"
          value={form.mediaType}
          onChange={(v) => setForm((f) => ({ ...f, mediaType: v as F['mediaType'] }))}
          options={MEDIA_CHOICES}
          disabled={narrow}
        />
        <FilterSelect
          label="Processing"
          value={form.lifecycle}
          onChange={(v) => setForm((f) => ({ ...f, lifecycle: v as F['lifecycle'] }))}
          options={LIFECYCLE_CHOICES}
          disabled={narrow}
        />
        <label className="min-w-0">
          <span className={LABEL_CLASS}>Uploaded from</span>
          <Input
            type="date"
            className="mt-1"
            value={form.from}
            onChange={(e) => setForm((f) => ({ ...f, from: e.target.value }))}
            disabled={!datesOk}
          />
        </label>
        <label className="min-w-0">
          <span className={LABEL_CLASS}>Uploaded to</span>
          <Input
            type="date"
            className="mt-1"
            value={form.to}
            onChange={(e) => setForm((f) => ({ ...f, to: e.target.value }))}
            disabled={!datesOk}
          />
        </label>
      </div>
      {!datesOk && !narrow && <p className="text-xs text-muted-foreground">{DATE_NEEDS_DATE_SORT_SENTENCE}</p>}
      {filtered && (
        <div>
          <Button size="sm" variant="ghost" onClick={onClear}>
            Clear filters
          </Button>
        </div>
      )}

      {actions && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">{actions}</div>}
    </div>
  );
}
